// FLAGS: personal-data
// Per-session client feedback for the logged-in expert: client email plus the free-text answer, which
// clients write without expecting a named reader.
import { FC, useEffect, useState } from "react";
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router";
import { isEmptyQueryResult } from "@src/queries/utils";
import NothingFound from "@src/components/NothingFound";
import { formatFirstUpperCase, formatStandardDate } from "@src/utils/formatter";
import {
  ConsultReviewQuestionsKeys,
  FinancialWellbeingConsultReviewQuestionsKeys,
} from "@src/constants";
import { useExpertFeedbackQuery } from "@src/queries/experts";
import { useTranslation } from "react-i18next";
import { ThumbsUpDownOutlined } from "@mui/icons-material";
import { DateFilter } from "@src/models/DateFilter";
import DateFilterDropdown from "@src/components/DateFilterDropdown";
import { ResponseConsultsConsultFeedbackResponse } from "@src/api/v1/data-contracts";

const MyFeedbacksView: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [dateFilter, setDateFilter] = useState<DateFilter>(DateFilter.AllTime);

  // Fetch feedbacks using the custom hook
  const { data: feedbacks, isFetching } = useExpertFeedbackQuery(
    dateFilter.valueOf(),
    page,
  );
  const [expandedRowConsultId, setExpandedRowConsultId] = useState<number>();

  const formatTooltip = (key: string) => t(key).replace(" <0>*</0>", "");

  const findAnswerValue = (
    consultFeedback: ResponseConsultsConsultFeedbackResponse,
    questionKeys: string[],
  ) =>
    consultFeedback.answers.find((answer) =>
      questionKeys.includes(answer.questionKey),
    )?.value ?? "";

  const dateFilterItems = Object.values(DateFilter);

  const columns: {
    id: string;
    minWidth: number;
    tooltip?: string;
  }[] = [
    {
      id: "MyFeedbacks.DateTime",
      minWidth: 80,
    },
    {
      id: "MyFeedbacks.Client",
      minWidth: 250,
    },
    {
      id: "MyFeedbacks.SessionType",
      minWidth: 100,
    },
    {
      id: "MyFeedbacks.Q1",
      minWidth: 50,
      tooltip: formatTooltip(t("Consult.Review.Question1")),
    },
    {
      id: "MyFeedbacks.Q2",
      minWidth: 50,
      tooltip: formatTooltip(t("Consult.Review.Question2")),
    },
    {
      id: "MyFeedbacks.Q3",
      minWidth: 50,
      tooltip: formatTooltip(t("Consult.Review.Question3")),
    },
    {
      id: "MyFeedbacks.Q4",
      minWidth: 50,
      tooltip: t("Consult.Review.Feedback"),
    },
  ];

  const handleRowClick = (
    event: React.MouseEvent<HTMLTableRowElement>,
    consultFeedback: ResponseConsultsConsultFeedbackResponse,
  ) => {
    event.stopPropagation();
    if (consultFeedback) {
      navigate(`/clients/${consultFeedback.clientId}`);
    }
  };

  function handleFeedbackClick(
    event:
      | React.MouseEvent<HTMLSpanElement>
      | React.KeyboardEvent<HTMLSpanElement>,
    consultFeedback: ResponseConsultsConsultFeedbackResponse,
  ): void {
    event.stopPropagation();
    if (expandedRowConsultId === consultFeedback.consultId) {
      setExpandedRowConsultId(undefined);
    } else {
      setExpandedRowConsultId(consultFeedback.consultId);
    }
  }

  const consultTypeCell = (
    consultFeedback: ResponseConsultsConsultFeedbackResponse,
  ) => {
    return (
      <TableCell>
        <span className="py-2 sub1">
          {formatFirstUpperCase(consultFeedback.consultType)}
        </span>
      </TableCell>
    );
  };

  const consultDateCell = (
    consultFeedback: ResponseConsultsConsultFeedbackResponse,
  ) => {
    return (
      <TableCell>
        <span className="py-2 sub1">
          {formatStandardDate(consultFeedback.startedAt, "Europe/Amsterdam")}
        </span>
      </TableCell>
    );
  };

  const consultClientCell = (
    consultFeedback: ResponseConsultsConsultFeedbackResponse,
  ) => {
    return (
      <TableCell>
        <span className="py-2 sub1 hover:text-blue-600">
          {consultFeedback.clientEmail}
        </span>
      </TableCell>
    );
  };

  const consultQ1Cell = (
    consultFeedback: ResponseConsultsConsultFeedbackResponse,
  ) => {
    return (
      <TableCell>
        <span className="py-2 sub1">
          {findAnswerValue(consultFeedback, [
            ConsultReviewQuestionsKeys.Question1,
            FinancialWellbeingConsultReviewQuestionsKeys.Question1,
          ])}
        </span>
      </TableCell>
    );
  };

  const consultQ2Cell = (
    consultFeedback: ResponseConsultsConsultFeedbackResponse,
  ) => {
    return (
      <TableCell>
        <span className="py-2 sub1">
          {findAnswerValue(consultFeedback, [
            ConsultReviewQuestionsKeys.Question2,
            FinancialWellbeingConsultReviewQuestionsKeys.Question2,
          ])}
        </span>
      </TableCell>
    );
  };

  const consultQ3Cell = (
    consultFeedback: ResponseConsultsConsultFeedbackResponse,
  ) => {
    return (
      <TableCell>
        <span className="py-2 sub1">
          {findAnswerValue(consultFeedback, [
            ConsultReviewQuestionsKeys.Question3,
            FinancialWellbeingConsultReviewQuestionsKeys.Question3,
          ])}
        </span>
      </TableCell>
    );
  };

  const consultFeedbackCell = (
    consultFeedback: ResponseConsultsConsultFeedbackResponse,
  ) => {
    const consultFeedbackValue = findAnswerValue(consultFeedback, [
      ConsultReviewQuestionsKeys.Question4,
      FinancialWellbeingConsultReviewQuestionsKeys.Question4,
    ]);

    return (
      <TableCell
        onMouseUp={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        <span
          key={consultFeedback.consultId}
          data-testid={`consult-feedback-text-${consultFeedback.consultId}`}
          className={
            consultFeedback.consultId === expandedRowConsultId
              ? "py-2 sub1" // expanded
              : "py-2 line-clamp-2 sub1 max-h-14"
          }
          role="button"
          // makes the span reachable by keyboard (Tab), so onKeyDown can fire
          tabIndex={0}
          onClick={(event) => handleFeedbackClick(event, consultFeedback)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleFeedbackClick(event, consultFeedback);
            }
          }}
        >{`${consultFeedbackValue}`}</span>
      </TableCell>
    );
  };

  const handleFilterSelect = (filter: DateFilter) => {
    setDateFilter(filter);
  };

  useEffect(() => {}, [expandedRowConsultId]);

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <DateFilterDropdown
          items={dateFilterItems}
          onSelectDateFilter={handleFilterSelect}
        />
      </Grid>
      {isEmptyQueryResult(feedbacks) ? (
        <NothingFound translationKey="MyFeedbacks.List.NotFound" />
      ) : (
        <Grid item xs={12}>
          <p className="px-3 py-4 rounded-md">
            <Box display="flex" alignItems="center">
              <ThumbsUpDownOutlined className="mr-2" />
              <h5 className="h5">{t("MyFeedbacks.Title")}</h5>
              {!!feedbacks?.pagination.total && (
                <TablePagination
                  component="div"
                  className="ml-auto -mr-2"
                  count={feedbacks.pagination.total}
                  rowsPerPage={feedbacks.pagination.limit}
                  rowsPerPageOptions={[10]}
                  page={feedbacks.pagination.currentPage}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  slotProps={{
                    actions: {
                      nextButton: {
                        disabled:
                          isFetching || !feedbacks.pagination.hasNextPage,
                      },
                      previousButton: {
                        disabled:
                          isFetching || feedbacks.pagination.currentPage === 0,
                      },
                    },
                  }}
                />
              )}
            </Box>
            {!isFetching && !!feedbacks?.pagination.total && (
              <TableContainer>
                <Table
                  data-testid="feedbacks"
                  stickyHeader
                  aria-label="sticky table"
                >
                  <TableHead>
                    <TableRow>
                      {columns.map((column) =>
                        column.tooltip ? (
                          <Tooltip
                            title={column.tooltip}
                            key={column.id}
                            placement="top"
                          >
                            <TableCell
                              key={column.id}
                              style={{ minWidth: column.minWidth }}
                              className="pl-1 py-2 sub1"
                            >
                              {t(column.id)}
                            </TableCell>
                          </Tooltip>
                        ) : (
                          <TableCell
                            key={column.id}
                            style={{ minWidth: column.minWidth }}
                            className="pl-1 py-2 sub1"
                          >
                            {t(column.id)}
                          </TableCell>
                        ),
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {feedbacks?.data?.map((consultFeedback) => (
                      <TableRow
                        data-testId="feedbacks-row"
                        hover
                        role="checkbox"
                        className="opacity-70 cursor-pointer"
                        tabIndex={-1}
                        key={consultFeedback.consultId.toString()}
                        id={consultFeedback.consultId.toString()}
                        onClick={(event) =>
                          handleRowClick(event, consultFeedback)
                        }
                      >
                        {consultDateCell(consultFeedback)}
                        {consultClientCell(consultFeedback)}
                        {consultTypeCell(consultFeedback)}
                        {consultQ1Cell(consultFeedback)}
                        {consultQ2Cell(consultFeedback)}
                        {consultQ3Cell(consultFeedback)}
                        {consultFeedbackCell(consultFeedback)}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </p>
        </Grid>
      )}
    </Grid>
  );
};

export default MyFeedbacksView;
